import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../../contexts/OrganizationContext';
import { fetchMissions } from '../../services/missionService';
import { getUserByGuid } from '../../services/userService';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Button, CircularProgress } from '@mui/material';

const PilotPerformance = () => {
    const navigate = useNavigate();
    const { selectedOrg, updateSelectedOrg } = useOrganization();
    const [missions, setMissions] = useState([]);
    const [pilots, setPilots] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadPilotPerformance = async () => {
            try {
                // Fetch all missions
                const missionData = await fetchMissions(selectedOrg);
                setMissions(missionData);

                // Load pilot details for assigned missions
                const pilotGuids = [...new Set(missionData.map((mission) => mission.picGuid))];
                const pilotDetails = await Promise.all(
                    pilotGuids.map(async (guid) => {
                        try {
                            return { guid, data: await getUserByGuid(guid) };
                        } catch {
                            return { guid, data: null }; // Handle missing pilots gracefully
                        }
                    })
                );

                // Store pilot details in a dictionary for easy access
                const pilotDict = pilotDetails.reduce((acc, { guid, data }) => {
                    if (data) acc[guid] = data;
                    return acc;
                }, {});

                setPilots(pilotDict);
            } catch (err) {
                console.error('Error loading pilot performance:', err);
            } finally {
                setLoading(false);
            }
        };

        loadPilotPerformance();
    }, []);

    // Calculate flight data for each pilot
    const pilotPerformance = Object.entries(pilots).map(([guid, pilot]) => {
        const pilotMissions = missions.filter((mission) => mission.picGuid === guid);
        const totalMissions = pilotMissions.length;
        const completedMissions = pilotMissions.filter((mission) => mission.completedAt).length;
        const inProgressMissions = pilotMissions.filter((mission) => mission.startedAt && !mission.completedAt).length;
        const notStartedMissions = pilotMissions.filter((mission) => !mission.startedAt).length;
        const totalFlightHours = pilotMissions.reduce((sum, mission) => {
            if (mission.startedAt && mission.completedAt) {
                const start = new Date(mission.startedAt);
                const end = new Date(mission.completedAt);
                return sum + (end - start) / (1000 * 60 * 60); // Convert milliseconds to hours
            }
            return sum;
        }, 0);

        const fullName = `${pilot.firstName || ''} ${pilot.lastName || ''}`.trim() || 'Unknown Pilot';

        return {
            name: fullName,
            email: pilot.email,
            totalMissions,
            completedMissions,
            inProgressMissions,
            notStartedMissions,
            totalFlightHours: totalFlightHours.toFixed(2),
        };
    });

    const handleBackToReports = () => {
        navigate('/reports');
    };

    return (
        <Box sx={{ padding: '16px' }}>
            {/* Back to Reports Button */}
            <Button
                variant="text"
                onClick={handleBackToReports}
                startIcon={<ArrowBackIcon />}
                sx={{
                    marginBottom: '16px',
                    color: '#2c3e50',
                    textTransform: 'none',
                    fontWeight: 400,
                    '&:hover': {
                        backgroundColor: '#f0f0f0',
                    },
                }}
            >
                Reports
            </Button>

            <Typography variant="h4" gutterBottom>
                Pilot Performance Report
            </Typography>

            {/* Show loading spinner while fetching data */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Pilot Table */}
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Pilot Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Total Missions</TableCell>
                                <TableCell>Completed Missions</TableCell>
                                <TableCell>In Progress Missions</TableCell>
                                <TableCell>Not Started Missions</TableCell>
                                <TableCell>Total Flight Hours</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pilotPerformance.map((pilot, index) => (
                                <TableRow key={index}>
                                    <TableCell>{pilot.name}</TableCell>
                                    <TableCell>{pilot.email || 'N/A'}</TableCell>
                                    <TableCell>{pilot.totalMissions}</TableCell>
                                    <TableCell>{pilot.completedMissions}</TableCell>
                                    <TableCell>{pilot.inProgressMissions}</TableCell>
                                    <TableCell>{pilot.notStartedMissions}</TableCell>
                                    <TableCell>{pilot.totalFlightHours} hrs</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>
            )}
        </Box>
    );
};

export default PilotPerformance;